import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Microphone, Stop, Play, Pause, DownloadSimple, Trash,
  SpeakerHigh, ArrowsClockwise, Waveform, Record, MagicWand, ShareFat
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';
import { EarlyAccessBanner } from '../components/EarlyAccessBanner';
import { shareOrDownload } from '../utils/share';

const API = process.env.REACT_APP_BACKEND_URL;

const AI_VOICES = [
  { id: 'alloy', name: 'Alloy', desc: 'Neutral, balanced' },
  { id: 'ash', name: 'Ash', desc: 'Clear, articulate' },
  { id: 'coral', name: 'Coral', desc: 'Warm, friendly' },
  { id: 'echo', name: 'Echo', desc: 'Smooth, calm' },
  { id: 'fable', name: 'Fable', desc: 'Expressive, storytelling' },
  { id: 'nova', name: 'Nova', desc: 'Energetic, upbeat' },
  { id: 'onyx', name: 'Onyx', desc: 'Deep, authoritative' },
  { id: 'sage', name: 'Sage', desc: 'Wise, measured' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Bright, cheerful' },
];

export default function VoiceoverPage() {
  const [tab, setTab] = useState('tts');
  // Zero Friction: Customise → progressive disclosure for Speed
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Optional reference audio
  const [refMedia, setRefMedia] = useState([]);
  const [script, setScript] = useState('');
  const [selectedVoice, setSelectedVoice] = useState(AI_VOICES[0]);
  const [speed, setSpeed] = useState(1.0);
  const [generating, setGenerating] = useState(false);
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewingVoice, setPreviewingVoice] = useState(null);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recPlaying, setRecPlaying] = useState(false);

  const audioRef = useRef(null);
  const recAudioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/voiceover/history`);
      setHistory(data.voiceovers || []);
    } catch {}
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Preview voice sample
  const previewVoice = async (voiceId, e) => {
    e.stopPropagation();
    if (previewingVoice === voiceId) {
      // Stop playing
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.currentTime = 0;
      }
      setPreviewingVoice(null);
      return;
    }
    setPreviewingVoice(voiceId);
    try {
      const url = `${API}/api/voiceover/preview/${voiceId}`;
      if (!previewAudioRef.current) {
        previewAudioRef.current = new Audio();
      }
      previewAudioRef.current.src = url;
      previewAudioRef.current.onended = () => setPreviewingVoice(null);
      previewAudioRef.current.onerror = () => { setPreviewingVoice(null); toast.error('Preview not available yet, try again'); };
      await previewAudioRef.current.play();
    } catch {
      setPreviewingVoice(null);
    }
  };

  const generateVoiceover = async () => {
    if (!script.trim()) { toast.error('Enter text for voiceover'); return; }
    if (script.length > 4096) { toast.error('Text must be under 4096 characters'); return; }

    setGenerating(true);
    setGeneratedAudioUrl(null);
    try {
      const { data } = await axios.post(`${API}/api/voiceover/generate`, {
        text: script,
        voice: selectedVoice.id,
        speed,
        model: 'tts-1-hd',
      });
      const audioUrl = `${API}${data.url}`;
      setGeneratedAudioUrl(audioUrl);
      toast.success('Voiceover generated! (2 credits)');
      loadHistory();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Generation failed');
    }
    setGenerating(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  // Recording methods
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const C = {
    bg: '#080808', card: '#141414', text: '#f5f0e8', muted: 'rgba(245,240,232,0.55)',
    dim: 'rgba(245,240,232,0.3)', accent: '#a78bfa', border: 'rgba(255,255,255,0.07)',
  };

  return (
    <DashboardLayout>
      <EarlyAccessBanner />
      <div className="max-w-4xl mx-auto space-y-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <BrandSettingsSummary fields={['brand','tone']} />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: C.text }}>Voiceover Studio</h1>
            <p className="text-sm mt-1" style={{ color: C.dim }}>Professional voice audio — in seconds</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {[{ id: 'tts', label: 'AI Voice', icon: <MagicWand size={14} /> }, { id: 'record', label: 'Record', icon: <Microphone size={14} /> }, { id: 'history', label: 'My Voiceovers', icon: <SpeakerHigh size={14} /> }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all"
              style={{
                background: tab === t.id ? 'rgba(167,139,250,0.08)' : 'transparent',
                border: `1px solid ${tab === t.id ? 'rgba(167,139,250,0.25)' : C.border}`,
                color: tab === t.id ? C.accent : C.dim,
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === 'tts' && (
          <div className="space-y-5">
            {/* Script input */}
            <div className="rounded-2xl p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <label className="text-xs font-medium mb-2 block" style={{ color: C.muted }}>Script</label>
              <textarea
                data-testid="voiceover-script"
                value={script}
                onChange={e => setScript(e.target.value)}
                placeholder="Type or paste the text you want to convert to speech..."
                rows={5}
                className="w-full bg-transparent text-sm resize-none outline-none"
                style={{ color: C.text }}
              />
              <p className="text-right text-[10px] mt-1" style={{ color: C.dim }}>{script.length}/4096</p>
            </div>

            {/* Voice selection */}
            <div className="rounded-2xl p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <label className="text-xs font-medium mb-3 block" style={{ color: C.muted }}>Choose Voice</label>
              <div className="grid grid-cols-3 gap-2">
                {AI_VOICES.map(v => (
                  <div key={v.id} className="relative">
                    <div onClick={() => setSelectedVoice(v)}
                      data-testid={`voice-${v.id}`}
                      className="w-full p-3 rounded-lg text-left transition-all cursor-pointer"
                      style={{
                        background: selectedVoice.id === v.id ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${selectedVoice.id === v.id ? 'rgba(167,139,250,0.25)' : C.border}`,
                      }}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold" style={{ color: selectedVoice.id === v.id ? C.accent : C.text }}>{v.name}</p>
                        <button onClick={(e) => previewVoice(v.id, e)}
                          data-testid={`preview-voice-${v.id}`}
                          className="w-6 h-6 rounded-full flex items-center justify-center transition-all"
                          style={{
                            background: previewingVoice === v.id ? 'rgba(167,139,250,0.2)' : 'rgba(255,255,255,0.05)',
                          }}>
                          {previewingVoice === v.id ? (
                            <Pause size={10} weight="fill" style={{ color: C.accent }} />
                          ) : (
                            <Play size={10} weight="fill" style={{ color: C.muted }} />
                          )}
                        </button>
                      </div>
                      <p className="text-[9px] mt-0.5" style={{ color: C.dim }}>{v.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Speed control — hidden until Customise expanded */}
            <ReferenceMediaUpload
              label="Add reference audio (optional)"
              note="Upload a clip for tone and pacing guidance."
              acceptedTypes={['audio']}
              maxFiles={1}
              maxSizeMB={10}
              onChange={setRefMedia}
            />

            {/* Generate button */}
            <button onClick={generateVoiceover} disabled={generating || !script.trim()}
              data-testid="generate-voiceover-btn"
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: C.accent, color: '#080808' }}>
              {generating ? (
                <><ArrowsClockwise size={16} className="animate-spin" /> Recording your voiceover…</>
              ) : (
                <><SpeakerHigh size={16} weight="fill" /> Create my voiceover · 2 credits</>
              )}
            </button>

            <button onClick={() => setShowAdvanced(v => !v)} data-testid="vo-customise-toggle"
              className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
              <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {showAdvanced ? 'Hide customise' : 'Customise →'}
              </span>
              <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
            </button>

            {showAdvanced && (
              <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: C.card, border: `1px solid ${C.border}` }}>
                <label className="text-xs font-medium" style={{ color: C.muted }}>Speed</label>
                <input type="range" min="0.5" max="2.0" step="0.1" value={speed}
                  onChange={e => setSpeed(parseFloat(e.target.value))} className="flex-1" />
                <span className="text-xs font-medium" style={{ color: C.text }}>{speed.toFixed(1)}x</span>
              </div>
            )}

            {/* Generated audio player */}
            {generatedAudioUrl && (
              <div className="rounded-2xl p-5" style={{ background: C.card, border: `1px solid rgba(167,139,250,0.2)` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={togglePlay} data-testid="play-generated"
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(167,139,250,0.15)' }}>
                      {playing ? <Pause size={18} style={{ color: C.accent }} /> : <Play size={18} style={{ color: C.accent }} />}
                    </button>
                    <div>
                      <p className="text-sm font-medium" style={{ color: C.text }}>Generated Voiceover</p>
                      <p className="text-[10px]" style={{ color: C.dim }}>{selectedVoice.name} &middot; {speed.toFixed(1)}x</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={generatedAudioUrl} download={`scalitos-voiceover.mp3`}
                      data-testid="download-voiceover"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
                      style={{ background: 'rgba(167,139,250,0.08)', color: C.accent, border: '1px solid rgba(167,139,250,0.2)' }}>
                      <DownloadSimple size={14} /> Download
                    </a>
                    <button onClick={() => shareOrDownload(
                      generatedAudioUrl, 'audio/mpeg', 'Voiceover'
                    )} data-testid="share-voiceover-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
                      style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
                      <ShareFat size={14} /> Share
                    </button>
                  </div>
                </div>
                <audio ref={audioRef} src={generatedAudioUrl}
                  onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} />
              </div>
            )}
          </div>
        )}

        {tab === 'record' && (
          <div className="space-y-5">
            <div className="rounded-2xl p-8 text-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              {!recording && !recordedUrl && (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.2)' }}>
                    <Microphone size={32} style={{ color: '#ef4444' }} />
                  </div>
                  <button onClick={startRecording} data-testid="start-recording"
                    className="px-6 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#ef4444', color: 'white' }}>
                    <Record size={14} className="inline mr-1.5" /> Start Recording
                  </button>
                </div>
              )}

              {recording && (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center animate-pulse" style={{ background: 'rgba(239,68,68,0.15)', border: '2px solid rgba(239,68,68,0.3)' }}>
                    <Waveform size={32} style={{ color: '#ef4444' }} />
                  </div>
                  <p className="text-lg font-medium" style={{ color: C.text }}>
                    {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </p>
                  <button onClick={stopRecording} data-testid="stop-recording"
                    className="px-6 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#ef4444', color: 'white' }}>
                    <Stop size={14} className="inline mr-1.5" /> Stop
                  </button>
                </div>
              )}

              {recordedUrl && !recording && (
                <div className="flex flex-col items-center gap-4">
                  <audio ref={recAudioRef} src={recordedUrl} onEnded={() => setRecPlaying(false)} />
                  <div className="flex gap-3">
                    <button onClick={() => { recPlaying ? recAudioRef.current?.pause() : recAudioRef.current?.play(); setRecPlaying(!recPlaying); }}
                      className="px-4 py-2 rounded-lg text-sm" style={{ background: 'rgba(167,139,250,0.08)', color: C.accent, border: '1px solid rgba(167,139,250,0.2)' }}>
                      {recPlaying ? <Pause size={14} className="inline mr-1" /> : <Play size={14} className="inline mr-1" />} {recPlaying ? 'Pause' : 'Play'}
                    </button>
                    <a href={recordedUrl} download="scalitos-recording.webm"
                      className="px-4 py-2 rounded-lg text-sm" style={{ background: 'rgba(167,139,250,0.08)', color: C.accent, border: '1px solid rgba(167,139,250,0.2)' }}>
                      <DownloadSimple size={14} className="inline mr-1" /> Download
                    </a>
                    <button onClick={() => { setRecordedUrl(null); setAudioBlob(null); setRecordingTime(0); }}
                      className="px-4 py-2 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.06)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.15)' }}>
                      <Trash size={14} className="inline mr-1" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="rounded-2xl p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <h3 className="text-sm font-medium mb-3" style={{ color: C.muted }}>All Voiceovers</h3>
            {history.length === 0 ? (
              <div className="text-center py-12">
                <SpeakerHigh size={32} className="mx-auto mb-3 opacity-20" style={{ color: C.text }} />
                <p className="text-xs" style={{ color: C.dim }}>No voiceovers yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((v, i) => (
                  <div key={v.voice_id || i} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}` }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate" style={{ color: C.text }}>{v.text}</p>
                      <p className="text-[9px]" style={{ color: C.dim }}>{v.voice} &middot; {v.speed}x &middot; {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <button onClick={() => { setScript(v.text); setSelectedVoice(AI_VOICES.find(av => av.id === v.voice) || AI_VOICES[0]); setSpeed(v.speed || 1.0); setTab('tts'); toast.success('Loaded from history'); }}
                        className="p-1.5 rounded-md hover:bg-white/5" title="Reload settings">
                        <Play size={14} style={{ color: C.accent }} />
                      </button>
                      <a href={`${API}${v.url}`} download className="p-1.5 rounded-md hover:bg-white/5" title="Download">
                        <DownloadSimple size={14} style={{ color: C.accent }} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
